/**
 * What the Agent runs panel's writes (pause, CI wiring, Save, Withdraw) share:
 * the mutation key and epoch bookkeeping that let each see the others, and
 * how each refreshes the panel. See `useProjectPanelMutations` for the rules.
 */

import type { QueryClient } from '@tanstack/react-query';
import { projectPanelKeys } from './useProjectPanel';
import { bumpPanelEpoch, panelEpoch } from './panelEpoch';
import type { PanelResponse } from '../types/projectPanel.types';

/** The panel's epoch when the write was sent. */
export interface WriteContext {
  epoch: number;
}

interface WriteOptions {
  mutationKey: readonly unknown[];
  onMutate: () => WriteContext;
  onSettled: () => void;
}

export function writeOptions(client: QueryClient, projectId: string): WriteOptions {
  return {
    mutationKey: projectPanelKeys.write(projectId),
    onMutate: () => ({ epoch: panelEpoch(client, projectId) }),
    onSettled: () => {
      bumpPanelEpoch(client, projectId);
    },
  };
}

export function refreshDefinitions(client: QueryClient, projectId: string): void {
  void client.invalidateQueries({ queryKey: projectPanelKeys.panel(projectId), exact: true });
  void client.invalidateQueries({ queryKey: projectPanelKeys.definitions(projectId) });
}

/**
 * A pause answer: stored only when surely the newest panel. It carries no
 * setup (the backend skips those GitLab reads for the safety lever, and a
 * pause changes none), so the setup already shown is kept.
 */
export async function storePanel(
  client: QueryClient,
  projectId: string,
  panel: PanelResponse,
  sent: WriteContext | undefined,
): Promise<void> {
  const key = projectPanelKeys.panel(projectId);
  // This write is still pending while its onSuccess runs: 1 is itself alone.
  const alone = client.isMutating({ mutationKey: projectPanelKeys.write(projectId) }) === 1;
  if (alone && sent !== undefined && sent.epoch === panelEpoch(client, projectId)) {
    await client.cancelQueries({ queryKey: key, exact: true });
    const shown = client.getQueryData<PanelResponse>(key);
    client.setQueryData(key, { ...panel, setup: panel.setup ?? shown?.setup ?? null });
  } else {
    void client.invalidateQueries({ queryKey: key, exact: true });
  }
  void client.invalidateQueries({ queryKey: projectPanelKeys.definitions(projectId) });
}
